import { isRecord } from './type-guards.js';

const SECOND_IN_MS = 1e3;
const MINUTE_IN_MS = 60 * SECOND_IN_MS;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;
const WEEK_IN_MS = 7 * DAY_IN_MS;
const MONTH_IN_MS = 30 * DAY_IN_MS;
const YEAR_IN_MS = 365 * DAY_IN_MS;

const NOW_THRESHOLD_MS = MINUTE_IN_MS;
const CLOCK_SKEW_TOLERANCE_MS = 5 * MINUTE_IN_MS;

type RelativeTimeUnit = Intl.RelativeTimeFormatUnit;
type RelativeTimeStyle = NonNullable<Intl.RelativeTimeFormatOptions['style']>;
type RelativeTimeNumeric = NonNullable<Intl.RelativeTimeFormatOptions['numeric']>;

export interface FormatterOptions {
  locale?: string;
  nowMs?: number;
  numeric?: RelativeTimeNumeric;
  style?: RelativeTimeStyle;
}

export interface RelativeTimeComputation {
  label: string;
  nextUpdateInMs: number;
  unit: RelativeTimeUnit;
  value: number;
}

const formatterCache = new Map<string, Intl.RelativeTimeFormat>();

const UNIT_TABLE: Array<{ upperBound: number; unit: RelativeTimeUnit; unitMs: number }> = [
  { upperBound: MINUTE_IN_MS, unit: 'second', unitMs: SECOND_IN_MS },
  { upperBound: HOUR_IN_MS, unit: 'minute', unitMs: MINUTE_IN_MS },
  { upperBound: DAY_IN_MS, unit: 'hour', unitMs: HOUR_IN_MS },
  { upperBound: WEEK_IN_MS, unit: 'day', unitMs: DAY_IN_MS },
  { upperBound: MONTH_IN_MS, unit: 'week', unitMs: WEEK_IN_MS },
  { upperBound: YEAR_IN_MS, unit: 'month', unitMs: MONTH_IN_MS },
];

/**
 * Convert a variety of timestamp representations into a millisecond epoch.
 */
export function parseTimestampToNumber(input: Date | string | number | undefined | null): number | null {
  if (input === undefined || input === null) {
    return null;
  }
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) {
      return null;
    }
    return input;
  }
  if (typeof input === 'string') {
    const value = Date.parse(input);
    return Number.isNaN(value) ? null : value;
  }
  if (isRecord<{ valueOf?: () => unknown }>(input) && typeof input.valueOf === 'function') {
    const candidate = input.valueOf();
    if (typeof candidate === 'number') {
      return Number.isNaN(candidate) ? null : candidate;
    }
  }
  return null;
}

function resolveTimestamp(input: Date | string | number | undefined | null): number | null {
  return parseTimestampToNumber(input);
}

/**
 * Convert a timestamp-like input into a valid Date instance when possible.
 */
export function parseTimestampToDate(input: Date | string | number | undefined | null): Date | null {
  const timestamp = parseTimestampToNumber(input);
  if (timestamp === null) {
    return null;
  }
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
}

function selectUnit(absMs: number): { unit: RelativeTimeUnit; unitMs: number } {
  for (const entry of UNIT_TABLE) {
    if (absMs < entry.upperBound) {
      return { unit: entry.unit, unitMs: entry.unitMs };
    }
  }
  return { unit: 'year', unitMs: YEAR_IN_MS };
}

function getFormatter(locale: string | undefined, style: RelativeTimeStyle, numeric: RelativeTimeNumeric) {
  const navigatorLocale = globalThis.navigator?.language;
  const resolvedLocale = locale || navigatorLocale || 'en';
  const key = `${resolvedLocale}|${style}|${numeric}`;
  const cached = formatterCache.get(key);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.RelativeTimeFormat(resolvedLocale, { style, numeric });
  formatterCache.set(key, formatter);
  return formatter;
}

/**
 * Produce relative-time metadata (value/unit/label) for a target timestamp.
 */
export function computeRelativeTime(
  targetMs: number,
  { locale, style = 'long', numeric = 'auto', nowMs = Date.now() }: FormatterOptions = {}
): RelativeTimeComputation {
  if (!Number.isFinite(targetMs)) {
    throw new TypeError('Invalid timestamp provided to computeRelativeTime');
  }

  const rawDiffMs = targetMs - nowMs;
  const diffMs = rawDiffMs > 0 && rawDiffMs <= CLOCK_SKEW_TOLERANCE_MS ? 0 : rawDiffMs;
  const absMs = Math.abs(diffMs);
  const formatter = getFormatter(locale, style, numeric);

  if (absMs < NOW_THRESHOLD_MS) {
    const label = formatter.format(0, 'second');
    const remaining = Math.max(SECOND_IN_MS, NOW_THRESHOLD_MS - absMs);
    return { value: 0, unit: 'second', label, nextUpdateInMs: remaining };
  }

  const { unit, unitMs } = selectUnit(absMs);
  const magnitude = Math.floor(absMs / unitMs) || 1;
  const value = diffMs >= 0 ? magnitude : -magnitude;
  const label = formatter.format(value, unit);

  const remainder = absMs % unitMs;
  let nextBoundary = remainder === 0 ? unitMs : remainder;
  if (diffMs < 0) {
    nextBoundary = remainder === 0 ? unitMs : unitMs - remainder;
  }
  const nextUpdateInMs = Math.max(SECOND_IN_MS, nextBoundary);

  return { value, unit, label, nextUpdateInMs };
}

/**
 * Resolve relative-time computation for a user-supplied timestamp.
 */
export function getRelativeTimeResult(
  date: Date | string | number | undefined | null,
  options?: FormatterOptions
): RelativeTimeComputation | null {
  const timestamp = resolveTimestamp(date);
  if (timestamp === null) {
    return null;
  }
  return computeRelativeTime(timestamp, options);
}

/**
 * Format a timestamp using Intl.RelativeTimeFormat with graceful fallbacks.
 */
export function formatRelativeTime(
  date: Date | string | number | undefined | null,
  options?: FormatterOptions
): string {
  const result = getRelativeTimeResult(date, options);
  if (!result) {
    return 'unknown time';
  }
  if (result.value === 0 && result.unit === 'second') {
    return 'just now';
  }
  return result.label;
}

export interface FormatRelativeTimeShortOptions extends FormatterOptions {
  fallback?: string;
}

/**
 * Generate compact relative phrases like "5m ago" or "in 2h".
 */
export function formatRelativeTimeShort(
  date: Date | string | number | undefined | null,
  { fallback = 'unknown time', ...options }: FormatRelativeTimeShortOptions = {}
): string {
  const result = getRelativeTimeResult(date, options);
  if (!result) {
    return fallback;
  }
  if (result.value === 0 && result.unit === 'second') {
    return 'just now';
  }

  const formatter = new Intl.RelativeTimeFormat(options.locale ?? 'en', {
    style: 'short',
    numeric: 'auto',
  });
  const formatted = formatter.format(result.value, result.unit);

  if (result.value > 0) {
    return formatted;
  }
  if (formatted === 'now') {
    return 'just now';
  }
  return `${formatted} ago`;
}

/**
 * Force past-tense phrasing for relative time output ("5 minutes ago").
 */
export function formatRelativeTimeAgo(
  date: Date | string | number | undefined | null,
  options?: FormatterOptions
): string {
  const result = getRelativeTimeResult(date, options);
  if (!result) {
    return 'unknown time';
  }
  if (result.value === 0 && result.unit === 'second') {
    return 'just now';
  }
  if (result.value > 0) {
    const style: RelativeTimeStyle = options?.style ?? 'long';
    const formatter = getFormatter(options?.locale, style, 'always');
    return formatter.format(-Math.abs(result.value), result.unit);
  }
  return result.label;
}

const COMPACT_UNIT_SUFFIX: Record<RelativeTimeUnit, string> = {
  second: 's',
  seconds: 's',
  minute: 'm',
  minutes: 'm',
  hour: 'h',
  hours: 'h',
  day: 'd',
  days: 'd',
  week: 'w',
  weeks: 'w',
  month: 'mo',
  months: 'mo',
  quarter: 'q',
  quarters: 'q',
  year: 'y',
  years: 'y',
};

export function formatCompactRelativeTime(result: RelativeTimeComputation): string {
  const suffix = COMPACT_UNIT_SUFFIX[result.unit] ?? result.unit.charAt(0);
  const magnitude = Math.abs(result.value);

  if (magnitude === 0) {
    return 'now';
  }

  return `${magnitude}${suffix}`;
}
